/**
 * Roles are assigned here, not in the portal. The portal tells us who someone
 * is; this system decides what they may do. Keeping authorisation local means
 * a portal misconfiguration cannot silently grant database access.
 *
 * Portal-supplied groups are recorded on the user for reference and can be
 * used to *suggest* a role to an admin, but never to set one automatically.
 */
export const ROLES = ['ANALYST', 'ANALYST_SENIOR', 'AUDITOR', 'ADMIN'] as const;
export type Role = (typeof ROLES)[number];

export const USER_STATUSES = ['ACTIVE', 'SUSPENDED', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * Capability names checked by the API's RequirePermission guard. Roles map to
 * sets of these so that adding a capability does not mean touching every
 * role check scattered through the codebase.
 */
export const PERMISSIONS = [
  'query:run',
  'query:cancel:own',
  'query:cancel:any',
  'export:csv',
  'catalog:read',
  'history:read:own',
  'history:read:any',
  'audit:read',
  'audit:verify',
  'datasource:read',
  'datasource:write',
  'policy:read',
  'policy:write',
  'user:read',
  'user:write',
  /**
   * Lifts column masking, so values are returned as they are stored.
   *
   * Held permanently by ADMIN, and grantable to anyone else per user for a
   * fixed period with a stated reason (see UNMASK_DURATIONS).
   *
   * It never lifts a DENY rule — a denied column stays denied for everyone,
   * including administrators. That boundary is what keeps this a masking
   * exemption rather than a switch that turns the policy engine off.
   *
   * Note the cost of giving it to ADMIN outright: administrators can read
   * every masked value without anyone approving it. What remains is
   * detection, not prevention — every such query writes a QUERY_RAN_UNMASKED
   * entry naming the columns, so the audit log still answers "who read real
   * addresses, and when".
   */
  'data:unmask',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  ANALYST: [
    'query:run',
    'query:cancel:own',
    'catalog:read',
    'history:read:own',
  ],
  ANALYST_SENIOR: [
    'query:run',
    'query:cancel:own',
    'export:csv',
    'catalog:read',
    'history:read:own',
  ],
  // An auditor can see everything that happened but cannot touch data.
  // Deliberately has no query:run — the person reviewing access should not
  // also hold it.
  AUDITOR: [
    'audit:read',
    'audit:verify',
    'history:read:any',
    'catalog:read',
    'policy:read',
    'datasource:read',
    'user:read',
  ],
  ADMIN: [
    'query:run',
    'query:cancel:own',
    'query:cancel:any',
    'export:csv',
    'catalog:read',
    'history:read:own',
    'history:read:any',
    'audit:read',
    'audit:verify',
    'datasource:read',
    'datasource:write',
    'policy:read',
    'policy:write',
    'user:read',
    'user:write',
    // Administrators see stored values, not masked ones. Still recorded on
    // every query, and still subject to DENY rules.
    'data:unmask',
  ],
};

export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export interface SessionUser {
  id: string;
  employeeId: string;
  email: string;
  name: string;
  department: string | null;
  role: Role;
  status: UserStatus;
  permissions: Permission[];
  /** Groups the portal asserted. Informational only. */
  portalGroups: string[];
  sessionExpiresAt: string;
}
